import DataType from '@opsbase/lib-datatype/DataType';
import i18n from '@opsbase/lib-i18n';
import Palette from '@opsbase/lib-palette';
import useTeamboardUpdate from '@opsbase/react-gql-hooks/teamboard/useTeamboardUpdate';
import PlaceChooser from '@opsbase/web-data-choosers/PlaceChooser/PlaceChooser';
import TeamChooser from '@opsbase/web-data-choosers/TeamChooser/TeamChooser';
import useSelection from '@opsbase/web-data-selection/useSelection';
import useSelectionActions from '@opsbase/web-data-selection/useSelectionActions';
import { Dialog } from '@opsbase/web-dialogs/Dialog';
import DialogSize from '@opsbase/web-dialogs/Dialog/DialogSize';
import Horizontal from '@opsbase/web-layout/Horizontal/Horizontal';
import Pane from '@opsbase/web-layout/Pane/Pane';
import Vertical from '@opsbase/web-layout/Vertical/Vertical';
import useNav from '@opsbase/web-nav/useNav/useNav';
import useParams from '@opsbase/web-nav/useParams';
import useNotificationActions from '@opsbase/web-notification/useNotificationActions';
import Button from '@opsbase/web-ui/Button/Button';
import { Checkbox } from '@opsbase/web-ui/Checkbox';
import Radio from '@opsbase/web-ui/Radio/Radio';
import { RadioGroup } from '@opsbase/web-ui/RadioGroup/RadioGroup';
import React, { useCallback, useMemo, useState } from 'react';
function TeamboardMultiEditDialog() {
    var _a;
    const { id } = useParams();
    const dataType = DataType.TEAMBOARD;
    const { selecteds } = useSelection(dataType);
    const { clearSelection } = useSelectionActions(dataType);
    const { raise } = useNotificationActions();
    const updateTeamboard = useTeamboardUpdate();
    const [changePlace, setChangePlace] = useState(false);
    const [changeTeam, setChangeTeam] = useState(false);
    const [changeSelfieRequired, setChangeSelfieRequired] = useState(false);
    const [changeShowStaffIssues, setChangeShowStaffIssues] = useState(false);
    const [busy, setBusy] = useState(false);
    const ids = useMemo(() => (id === 'selected' ? selecteds.map(o => o.id) : [id]), [id, selecteds]);
    const { goBack } = useNav();
    const [patch, setPatch] = useState({});
    const handleAction = useCallback(async () => {
        var _a;
        setBusy(true);
        if (patch === null || patch === void 0 ? void 0 : patch.selfieRequired)
            patch.selfieRequired = patch.selfieRequired === 'true';
        if (patch === null || patch === void 0 ? void 0 : patch.showStaffIssues)
            patch.showStaffIssues = patch.showStaffIssues === 'true';
        if (patch === null || patch === void 0 ? void 0 : patch.place) {
            patch.place = { id: patch.place.id };
        }
        if ((_a = patch === null || patch === void 0 ? void 0 : patch.teams) === null || _a === void 0 ? void 0 : _a.length) {
            patch.teams = patch.teams.map(t => ({ id: t.id }));
        }
        return raise(Promise.all(ids.map(theId => updateTeamboard({ id: theId, patch }))).finally(() => {
            clearSelection();
            setBusy(false);
            goBack();
        }), i18n.t('teamboard-edit-successful'));
    }, [patch, raise, ids, updateTeamboard, clearSelection, goBack]);
    const handleClose = useCallback(() => goBack(), [goBack]);
    const handleChangePlace = useCallback(async (place) => {
        if (!place || !place.length)
            return;
        return setPatch({
            ...patch,
            place: place[0],
            teams: [],
        });
    }, [patch]);
    const handleChangeShowStaffIssues = useCallback(async (value) => {
        return setPatch({
            ...patch,
            showStaffIssues: value,
        });
    }, [patch]);
    const handleChangeSelfieRequired = useCallback(async (value) => {
        return setPatch({
            ...patch,
            selfieRequired: value,
        });
    }, [patch]);
    const handleChangeTeam = useCallback(async (teams) => setPatch({
        ...patch,
        teams: teams,
        allTeams: teams.length ? false : true,
    }), [patch]);
    const handleSelectAllTeams = useCallback(async (flag) => {
        if (flag) {
            return setPatch({
                ...patch,
                allTeams: true,
                teams: [],
            });
        }
        else {
            return setPatch({
                ...patch,
                allTeams: false,
            });
        }
    }, [patch]);
    return (React.createElement(Dialog, { size: DialogSize.X800_Y550, title: i18n.t('edit-selected-teamboards'), padded: false, buttonsLeft: React.createElement(Button, { onClick: handleClose, color: Palette.Grey, disabled: busy, tabIndex: 1, label: i18n.t('cancel') }), buttonsRight: React.createElement(Button, { onClick: handleAction, disabled: busy, tabIndex: 0, label: i18n.t('make-updates') }), scroll: true },
        React.createElement(Pane, { paddingX: true },
            React.createElement(Vertical, { gutter: true },
                React.createElement(Checkbox, { label: i18n.t('change-teamboard-where'), color: Palette.GoodOldBlue, checked: changePlace, onChange: setChangePlace }),
                changePlace && (React.createElement(Pane, { paddingX: 16 },
                    React.createElement(PlaceChooser, { required: true, value: (patch === null || patch === void 0 ? void 0 : patch.place) ? [patch === null || patch === void 0 ? void 0 : patch.place] : [], onChange: handleChangePlace }))),
                React.createElement(Checkbox, { label: i18n.t('change-teamboard-teams'), color: Palette.GoodOldBlue, checked: changeTeam, onChange: setChangeTeam }),
                changeTeam && (React.createElement(Pane, { paddingX: 16 },
                    React.createElement(TeamChooser, { multi: true, required: true, value: (_a = patch.teams) !== null && _a !== void 0 ? _a : [], onChange: handleChangeTeam, allSelectable: true, allSelected: patch.allTeams, onSelectAll: handleSelectAllTeams, canCreate: true }))),
                React.createElement(Checkbox, { label: i18n.t('change-selfie-required'), color: Palette.GoodOldBlue, checked: changeSelfieRequired, onChange: setChangeSelfieRequired }),
                changeSelfieRequired && (React.createElement(Pane, { paddingX: 16 },
                    React.createElement(RadioGroup, { value: patch.selfieRequired, onChange: handleChangeSelfieRequired },
                        React.createElement(Horizontal, { justify: "flex-start", gutter: true, centered: true },
                            React.createElement(Radio, { label: i18n.t('selfie-required'), value: 'true', checked: patch.selfieRequired === 'true', color: Palette.positive }),
                            React.createElement(Radio, { label: i18n.t('selfie-not-required-any'), value: 'false', checked: patch.selfieRequired === 'false', color: Palette.warning }))))),
                React.createElement(Checkbox, { label: i18n.t('change-show-staff-issues'), color: Palette.GoodOldBlue, checked: changeShowStaffIssues, onChange: setChangeShowStaffIssues }),
                changeShowStaffIssues && (React.createElement(Pane, { paddingX: 16 },
                    React.createElement(RadioGroup, { value: patch.showStaffIssues, onChange: handleChangeShowStaffIssues },
                        React.createElement(Horizontal, { justify: "flex-start", gutter: true, centered: true },
                            React.createElement(Radio, { label: i18n.t('show-staff-issues-for-all'), value: 'true', checked: patch.showStaffIssues === 'true', color: Palette.positive }),
                            React.createElement(Radio, { label: i18n.t('do-not-show-staff-issues-any'), value: 'false', checked: patch.showStaffIssues === 'false', color: Palette.warning })))))))));
}
export default TeamboardMultiEditDialog;
